/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Typography, Box } from '@mui/material';
import { useState, useEffect } from 'react';
import type {
  GeoWebPluginInstance,
  PluginInternalState,
} from '@opengeoweb/plugin-interface';
import { PreBox } from './PreBox';
import { usePluginsTranslation } from '../../../utils/i18n';

interface PluginStateSectionProps {
  instance?: GeoWebPluginInstance | null;
  instanceId: string;
}

export const PluginStateSection: React.FC<PluginStateSectionProps> = ({
  instance,
  instanceId,
}) => {
  const { t } = usePluginsTranslation();
  const [state, setState] = useState<PluginInternalState | null>(null);

  useEffect(() => {
    if (!instance?.onPluginStateChange) {
      setState(null);
      return undefined;
    }
    const unsubscribe = instance.onPluginStateChange((state) =>
      setState(state),
    );
    return unsubscribe;
  }, [instance]);

  return (
    <Box data-testid="plugin-debug-plugin-state" sx={{ px: 2, py: 1 }}>
      <Typography gutterBottom variant="subtitle2">
        {t('plugin-internal-state-for-instance', { instanceId })}
      </Typography>
      {state && Object.keys(state).length > 0 ? (
        <PreBox data={state} />
      ) : (
        <Typography color="text.secondary" variant="body2">
          {t('no-internal-state-set')}
        </Typography>
      )}
    </Box>
  );
};
